import type { NormalizedReport } from "@/features/reports/model/normalized-report"
import { formatReportValue } from "@/features/reports/lib/field-formatters"

/** RFC-4180 cell escaping: quote when the value contains a comma, quote or newline; double embedded quotes. */
function escapeCsvCell(value: string): string {
  return /[",\n\r]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value
}

/** Serialize header + row arrays to a CSV string (CRLF line endings). */
export function toCsv(headers: string[], rows: string[][]): string {
  return [headers, ...rows].map((row) => row.map(escapeCsvCell).join(",")).join("\r\n")
}

/**
 * Build a CSV from a normalized report's columns + rows, using the caller-resolved column labels (i18n) and
 * the same per-value-type formatting the on-screen table uses, so the file matches what the user sees.
 */
export function reportToCsv(
  report: NormalizedReport,
  resolveLabel: (labelKey: string) => string,
  currency: string,
): string {
  const columns = report.columns ?? []
  const rows = report.rows ?? []
  const headers = columns.map((column) => resolveLabel(column.labelKey))
  const body = rows.map((row) => columns.map((column) => formatReportValue(row[column.key], column.valueType, currency)))
  return toCsv(headers, body)
}

/** UTF-8 byte-order mark so Excel reads accented characters correctly. */
const UTF8_BOM = "\uFEFF"

/** Trigger a browser download of text content (prepends a BOM so Excel reads UTF-8 correctly). */
export function downloadTextFile(fileName: string, content: string, mime = "text/csv;charset=utf-8"): void {
  downloadBlob(new Blob([`${UTF8_BOM}${content}`], { type: mime }), fileName)
}

/** Trigger a browser download of a blob. */
export function downloadBlob(blob: Blob, fileName: string): void {
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement("a")
  link.href = url
  link.download = fileName
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.URL.revokeObjectURL(url)
}
